import React, { useState, useEffect } from 'react';
import { CONTEXT_FRAME_NAMES, getIsLoggedIn } from '@nike/privacy-shared-logic';
import { TranslationsContextProvider, ErrorBoundary } from '@nike/privacy-shared-react';
import type { INikePrivacy, ContextFrameNamesModal } from '@nike/privacy-types';

import { CookieDialog } from './components/CookieDialog';
import { ErrorDialog } from './components/ErrorDialog';
import { PrivacyConsentContextProvider } from './components/PrivacyConsentContextProvider';
import { getRepresentation } from './helpers/representation';
import { initializeNikePrivacy, getInteractions } from './helpers/nikePrivacy';
import { PRIVACY_COMPONENT_TYPE, PRIVACY_CONSENT_TRANSLATIONS } from './constants';
import { useInitializePrivacyCoreError } from './hooks/useInitializePrivacyCoreError';
import { useInitializeInlineError } from './hooks/useInitializeInlineError';
import type { Representation } from './types';

declare global {
  interface Window {
    NikePrivacy: INikePrivacy;
    /**
     * Flag set by native apps used for determining which API implementation to use.
     * This property will only be present on pages have been included from a Nike mobile app via a specially-configured WebView.
     */
    MOBILE_TARGET_PLATFORM?: 'ios' | 'android';
  }
}

/**
 * @public
 * @name PrivacyConsent
 * @description Component which renders {@link CookieDialog}
 * @returns {JSX.Element|null}
 * @memberof mfe-privacy-consent
 */
const PrivacyConsent = (): JSX.Element | null => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [representationsFields, setRepresentationsFields] = useState<undefined | Representation>();
  const [hasInitializeError, setHasInitializeError] = useState(false);
  const [contextFrameName, setContextFrameName] = useState<undefined | ContextFrameNamesModal>(
    undefined,
  );

  const hasInitializePrivacyCoreError = useInitializePrivacyCoreError(contextFrameName);
  const hasInitializeInlineError = useInitializeInlineError(contextFrameName);

  const hasError = hasInitializeError || hasInitializePrivacyCoreError || hasInitializeInlineError;

  useEffect(() => {
    (async () => {
      try {
        const isLoggedInLocal = await getIsLoggedIn();
        const contextFrameNameLocale = isLoggedInLocal
          ? CONTEXT_FRAME_NAMES.WEB_MEMBER_MODAL
          : CONTEXT_FRAME_NAMES.WEB_GUEST_MODAL;
        setIsLoggedIn(isLoggedInLocal);
        setContextFrameName(contextFrameNameLocale);

        await initializeNikePrivacy();
        const context = getInteractions(contextFrameNameLocale);
        const representationsFields = getRepresentation(context, contextFrameNameLocale);
        setRepresentationsFields(representationsFields);
      } catch (error) {
        setHasInitializeError(true);
      }
    })();
  }, []);

  if (
    hasError ||
    !representationsFields ||
    representationsFields.type !== PRIVACY_COMPONENT_TYPE.MODAL ||
    !contextFrameName
  ) {
    return null;
  }

  return (
    <TranslationsContextProvider namespace={PRIVACY_CONSENT_TRANSLATIONS}>
      <PrivacyConsentContextProvider
        isLoggedIn={isLoggedIn}
        representationsFields={representationsFields}
        contextFrameName={contextFrameName}
      >
        <CookieDialog />
        <ErrorDialog />
      </PrivacyConsentContextProvider>
    </TranslationsContextProvider>
  );
};

/**
 * @public
 * @name PrivacyConsentWrapper
 * @description Checks if NikePrivacy exists on the window and it is enabled for use
 * @returns {JSX.Element|null}
 */
const PrivacyConsentWrapper = (): JSX.Element | null => {
  if (!window.NikePrivacy?.isEnabled || window.MOBILE_TARGET_PLATFORM) return null;

  return (
    <ErrorBoundary>
      <PrivacyConsent />
    </ErrorBoundary>
  );
};

export default PrivacyConsentWrapper;
